import { Button } from '@auctisync/ui';
import type { Tables } from '@auctisync/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

/**
 * Module #23 chunk 4 deferred follow-up — central queue of lot questions
 * across all of the staff's tenants.
 *
 * Before this page existed, tenant_owner/tenant_staff had to visit each
 * lot's public detail page to discover pending questions. The Q&A help
 * article called the dedicated queue out as "queued"; this is that queue.
 *
 * Default view: pending (unanswered + non-hidden), newest first.
 * Toggle: include answered questions in the list.
 * Hidden questions are NOT surfaced here (matches existing RLS — recovery
 * still requires super-admin DB access).
 *
 * Inline Answer + Hide actions mirror LotQA semantics. Both write the same
 * rows in public.lot_questions; the per-lot panel and this queue stay in
 * sync on the next fetch.
 */

type LotRef = Pick<Tables<'lots'>, 'id' | 'lot_number' | 'title' | 'auction_id'>;
type AuctionRef = Pick<Tables<'auctions'>, 'id' | 'slug' | 'title' | 'tenant_id'>;
type QuestionRow = Pick<
  Tables<'lot_questions'>,
  'id' | 'lot_id' | 'asker_user_id' | 'question_text' | 'answer_text' | 'answered_at' | 'created_at'
> & {
  lot: (LotRef & { auction: AuctionRef | null }) | null;
};

type AskerProfile = Pick<Tables<'profiles'>, 'id' | 'display_name'>;

export function AdminLotQuestions() {
  const { session } = useSession();
  const claims = readClaims(session);
  const userId = session?.user?.id ?? null;

  const [questions, setQuestions] = useState<QuestionRow[] | null>(null);
  const [askerNames, setAskerNames] = useState<Map<string, string>>(new Map());
  const [includeAnswered, setIncludeAnswered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [pendingAnswerId, setPendingAnswerId] = useState<string | null>(null);
  const [answerDraft, setAnswerDraft] = useState('');
  const [pendingHideId, setPendingHideId] = useState<string | null>(null);
  const [hideReason, setHideReason] = useState('');

  const load = useCallback(async () => {
    setError(null);
    const { data, error: err } = await supabase
      .from('lot_questions')
      .select(
        'id, lot_id, asker_user_id, question_text, answer_text, answered_at, created_at, lot:lots!lot_id(id, lot_number, title, auction_id, auction:auctions!auction_id(id, slug, title, tenant_id))',
      )
      .order('created_at', { ascending: false });
    if (err) {
      setError(err.message);
      setQuestions([]);
      return;
    }
    const rows = (data ?? []) as unknown as QuestionRow[];
    // Tenant scope: super_admin sees all; tenant staff see only their tenant's
    // questions. The lot_questions RLS doesn't tenant-scope reads (it just
    // hides hidden rows), so we filter client-side to keep the queue focused.
    const scoped = claims.isSuperAdmin
      ? rows
      : rows.filter((q) => q.lot?.auction?.tenant_id === claims.tenantId);
    setQuestions(scoped);

    // Resolve asker display names via profiles.
    const askerIds = Array.from(new Set(scoped.map((q) => q.asker_user_id)));
    if (askerIds.length > 0) {
      const { data: askerRows } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', askerIds);
      const map = new Map<string, string>();
      for (const a of (askerRows ?? []) as AskerProfile[]) {
        if (a.display_name) map.set(a.id, a.display_name);
      }
      setAskerNames(map);
    } else {
      setAskerNames(new Map());
    }
  }, [claims.isSuperAdmin, claims.tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (questions === null) return null;
    return includeAnswered ? questions : questions.filter((q) => q.answer_text === null);
  }, [questions, includeAnswered]);

  async function handleAnswer(questionId: string) {
    if (!userId || answerDraft.trim().length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: err } = await supabase
        .from('lot_questions')
        .update({
          answer_text: answerDraft.trim(),
          answered_at: new Date().toISOString(),
          answered_by: userId,
        })
        .eq('id', questionId);
      if (err) throw err;
      setPendingAnswerId(null);
      setAnswerDraft('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleHide(questionId: string) {
    setSubmitting(true);
    setError(null);
    try {
      const { error: err } = await supabase
        .from('lot_questions')
        .update({
          hidden_at: new Date().toISOString(),
          hidden_reason: hideReason.trim() || null,
        })
        .eq('id', questionId);
      if (err) throw err;
      setPendingHideId(null);
      setHideReason('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const pendingCount = (questions ?? []).filter((q) => q.answer_text === null).length;

  return (
    <main style={pageStyle}>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '2rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '0.25rem',
          }}
        >
          Bidder questions
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
          Pending questions across all your auctions. Answer or hide inline.
        </p>
      </header>

      <div
        style={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          marginBottom: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <strong
          style={{
            fontSize: '0.9rem',
            color: pendingCount > 0 ? 'var(--brand-accent)' : 'var(--text-muted)',
          }}
        >
          {pendingCount === 0
            ? 'No pending questions'
            : `${pendingCount} pending question${pendingCount === 1 ? '' : 's'}`}
        </strong>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.85rem',
            color: 'var(--text)',
          }}
        >
          <input
            type="checkbox"
            checked={includeAnswered}
            onChange={(e) => setIncludeAnswered(e.target.checked)}
          />
          Include answered
        </label>
      </div>

      {error ? (
        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-danger, #fef2f2)',
            color: 'var(--text-danger, #991b1b)',
            border: '1px solid var(--border-danger, #fecaca)',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      ) : null}

      {filtered === null ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: '2rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--r-card, 0.5rem)',
            fontSize: '0.875rem',
          }}
        >
          {includeAnswered
            ? 'No questions at all on any of your lots yet.'
            : "All caught up — no pending questions. Toggle 'Include answered' to see the history."}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {filtered.map((q) => {
            const lotLabel = q.lot
              ? `Lot #${q.lot.lot_number} · ${q.lot.title ?? '(untitled)'}`
              : 'Unknown lot';
            const lotHref =
              q.lot?.auction?.slug && q.lot.lot_number
                ? `/auctions/${q.lot.auction.slug}/lots/${q.lot.lot_number}`
                : null;
            const auctionLabel = q.lot?.auction?.title ?? '';
            const askerName = askerNames.get(q.asker_user_id) ?? 'Bidder';
            const isPendingHide = pendingHideId === q.id;
            const isPendingAnswer = pendingAnswerId === q.id;
            return (
              <article
                key={q.id}
                style={{
                  border: '1px solid var(--admin-hairline, var(--border))',
                  borderRadius: 'var(--r-card, 0.5rem)',
                  padding: '0.85rem 1rem',
                  background: 'var(--surface-elev)',
                }}
              >
                {isPendingHide ? (
                  <div>
                    <p
                      style={{
                        color: 'var(--text)',
                        marginBottom: '0.5rem',
                        fontSize: '0.9rem',
                      }}
                    >
                      Hide this question from bidders? This cannot be undone from the UI.
                    </p>
                    <p
                      style={{
                        color: 'var(--text-muted)',
                        fontSize: '0.85rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <strong>Q:</strong> {q.question_text}
                    </p>
                    <input
                      type="text"
                      value={hideReason}
                      onChange={(e) => setHideReason(e.target.value)}
                      placeholder="Optional internal reason (e.g. 'spam', 'PII')"
                      maxLength={200}
                      style={inputStyle}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          void handleHide(q.id);
                        }}
                        disabled={submitting}
                      >
                        {submitting ? 'Hiding…' : 'Hide question'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPendingHideId(null);
                          setHideReason('');
                        }}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.75rem',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        {lotHref ? (
                          <Link
                            to={lotHref}
                            style={{ color: 'var(--brand-accent)', fontWeight: 500 }}
                          >
                            {lotLabel}
                          </Link>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>{lotLabel}</span>
                        )}
                        {auctionLabel ? (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            {auctionLabel}
                          </div>
                        ) : null}
                      </div>
                      <div
                        style={{
                          textAlign: 'right',
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                        }}
                      >
                        <div>{askerName}</div>
                        <div>asked {relativeTime(q.created_at)}</div>
                      </div>
                    </div>
                    <p
                      style={{
                        color: 'var(--text)',
                        marginBottom: '0.5rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <strong>Q:</strong> {q.question_text}
                    </p>
                    {q.answer_text ? (
                      <p
                        style={{
                          color: 'var(--text-muted)',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        <strong>A:</strong> {q.answer_text}
                        {q.answered_at ? (
                          <span
                            style={{
                              fontSize: '0.75rem',
                              marginLeft: '0.5rem',
                              color: 'var(--text-muted)',
                            }}
                          >
                            ({relativeTime(q.answered_at)})
                          </span>
                        ) : null}
                      </p>
                    ) : isPendingAnswer ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <textarea
                          value={answerDraft}
                          onChange={(e) => setAnswerDraft(e.target.value)}
                          placeholder="Type your answer…"
                          rows={3}
                          maxLength={4000}
                          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              void handleAnswer(q.id);
                            }}
                            disabled={submitting || answerDraft.trim().length === 0}
                          >
                            {submitting ? 'Saving…' : 'Save answer'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setPendingAnswerId(null);
                              setAnswerDraft('');
                            }}
                            disabled={submitting}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : null}
                    {!isPendingAnswer ? (
                      <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.5rem' }}>
                        {!q.answer_text ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setPendingAnswerId(q.id);
                              setAnswerDraft('');
                            }}
                          >
                            Answer this
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPendingHideId(q.id);
                            setHideReason('');
                          }}
                        >
                          Hide
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const week = Math.floor(day / 7);
  if (week < 5) return `${week}w ago`;
  return new Date(iso).toLocaleDateString();
}

const pageStyle: React.CSSProperties = {
  padding: '2rem 1.25rem',
  maxWidth: '60rem',
  margin: '0 auto',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.85rem',
  fontFamily: 'inherit',
};
